import { Box, Grid, Tabs, Text, createStyles } from "@mantine/core";
import React, { useEffect, useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppNavbar from "../../../components/AppNavbar/AppNavbar";
import { useSelector } from "react-redux";
import {
  getPendingActionsSkypoint,
  getSkypointDashboardMetrics,
} from "../../../services/dashboard";
import { RxCross1 } from "react-icons/rx";
import SkypointUploadStaffDocDashboard from "../../../components/SkypointStaffDetails/SkypointUploadStaffDocDashboard";
import SkypointPendingAction from "./SkypointPendingAction";
import { ActivityTable } from "./ActivityTable";
import { getSkypointProjectTaskStatus } from "../../../services/skypointProject";
import ComplianceSnapshot from "./ComplianceSnapshot";
import HighRiskProviders from "./HighRiskProviders";
import { isStaff } from "../../../http";
import {
  IconLayoutDashboard,
  IconHeartRateMonitor,
  IconChartBar,
} from "@tabler/icons-react";

// Lightweight placeholder shown in tabs that haven't been built out yet.
// Inlined here to avoid scattering a one-off shell into a new file.
const ComingSoon = ({ title, description }) => (
  <Box
    sx={{
      background: "#FFFFFF",
      border: "1px solid #EAECF0",
      borderRadius: 12,
      padding: "64px 24px",
      textAlign: "center",
    }}
  >
    <Text
      sx={{
        fontFamily: "Inter",
        fontSize: 18,
        fontWeight: 600,
        color: "#101828",
      }}
    >
      {title} — coming soon
    </Text>
    <Text
      sx={{
        fontFamily: "Inter",
        fontSize: 13,
        color: "#667085",
        marginTop: 6,
        maxWidth: 480,
        marginLeft: "auto",
        marginRight: "auto",
      }}
    >
      {description}
    </Text>
  </Box>
);

// Tile keys → detail-page routes. Every tile in the command center now has
// a dedicated detail page; the dashboard tab-switch fallback is gone.
const ROUTE_MAP = {
  criticalAlerts: "/home/skypoint-alerts/critical",
  overdueTasks: "/home/skypoint-alerts/overdue-tasks",
  overdueNotes: "/home/skypoint-alerts/overdue-notes",
  overduePendingNotes: "/home/skypoint-alerts/pending-notes",
  missingCarePlans: "/home/skypoint-alerts/missing-care-plans",
  expiringDocuments: "/home/skypoint-alerts/expiring-documents",
  marCompletion: "/home/skypoint-alerts/mar-completion",
  docCompletion: "/home/skypoint-alerts/doc-completion",
};


const useStyles = createStyles((theme) => ({
  container: {
    width: "100%",
  },
  stickyBanner: {
    position: "sticky",
    top: 0,
    zIndex: 100,
  },
  welcomeBanner: {
    borderRadius: 8,
    border: "1px solid #E4E4E7",
    background: "#FFF",
    padding: "16px 20px",
    margin: "15px 28px",
    boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    [theme.fn.smallerThan("sm")]: {
      margin: "10px 12px",
      padding: "12px 14px",
    },
  },
  welcomeText: {
    fontFamily: "Inter",
    fontSize: 15,
    fontWeight: 500,
    color: "#101828",
    lineHeight: 1.4,
  },
  welcomeSubtext: {
    fontFamily: "Inter",
    fontSize: 13,
    fontWeight: 400,
    color: "#667085",
    marginTop: 2,
    [theme.fn.smallerThan("xs")]: {
      display: "none",
    },
  },
  welcomeClose: {
    cursor: "pointer",
    color: "#98A2B3",
    flexShrink: 0,
    padding: 4,
    borderRadius: 4,
    "&:hover": {
      color: "#667085",
      backgroundColor: "#F2F4F7",
    },
  },
  content: {
    padding: "24px 28px",
    [theme.fn.smallerThan("sm")]: {
      padding: "16px 12px",
    },
  },
  pendingActionContainer: {
    // The new compact Pending Action panel self-sizes from its content +
    // 560px scroll cap, so we no longer need to force a 645px frame.
  },
  tabList: {
    borderBottom: "1px solid #EAECF0",
    gap: 0,
    // Only three tabs post-redesign; they always fit. Disable the scroll
    // affordance Mantine adds when the list overflows.
    overflowX: "visible",
    flexWrap: "wrap",
  },
  tab: {
    fontFamily: "Inter",
    fontSize: 13,
    fontWeight: 500,
    color: "#667085",
    padding: "10px 16px",
    borderBottom: "2px solid transparent",
    whiteSpace: "nowrap",
    "&[data-active]": {
      color: "#7F56D9",
      borderBottomColor: "#7F56D9",
      fontWeight: 600,
    },
    "&:hover": {
      backgroundColor: "#F9FAFB",
    },
    [theme.fn.smallerThan("sm")]: {
      padding: "8px 10px",
      fontSize: 12,
    },
  },
  tabPanel: {
    paddingTop: 20,
  },
}));

export const SkyPointDashboard = ({ isSidebarOpen }) => {
  const navigate = useNavigate();
  const { profile } = useSelector((state) => state?.profile);
  const { classes } = useStyles();
  const { selectedDocModal } = useSelector((state) => state?.document);
  const { dashboardMetrics, dashboardMetricsLoading } = useSelector(
    (state) => state.dashboard
  );
  const [activeTab, setActiveTab] = useState("command-center");
  const [showWelcome, setShowWelcome] = useState(true);

  const userIsStaff = isStaff(profile?.user?.role);

  const canSee = useCallback(
    (permKey) => {
      if (!userIsStaff) return true;
      const permissions = profile?.user?.userpermissions || {};
      return permissions[permKey] !== false;
    },
    [userIsStaff, profile?.user?.userpermissions]
  );

  useEffect(() => {
    getSkypointDashboardMetrics();
    getPendingActionsSkypoint();
    // Nothing here reads the provider list; this used to load it whole ("all", stopped at 200).
    // The provider pickers search the server themselves.
    getSkypointProjectTaskStatus();
  }, []);

  const handleProviderClick = () => {
    // Reserved — HighRiskProviders row click currently no-ops.
  };

  const handleTileClick = (tileKey) => {
    const route = ROUTE_MAP[tileKey];
    if (route) navigate(route);
  };

  // Only Command Center, Operational Health, and Business Metrics tabs exist
  // post-redesign. Operational + Business are placeholders ("coming soon").
  const visibleTabs = ["command-center"];
  if (canSee("dashboardOperational")) visibleTabs.push("operational");
  if (canSee("dashboardBusinessMetrics")) visibleTabs.push("business");

  return (
    <Box className={classes.container}>
      {/* ─── STICKY HOME BANNER ─── */}
      <Box className={classes.stickyBanner}>
        <AppNavbar title="Home" profile={profile} isSidebarOpen={isSidebarOpen} />
      </Box>

      {/* ─── DISMISSIBLE WELCOME BANNER ─── */}
      {showWelcome && (
        <Box className={classes.welcomeBanner}>
          <Box>
            <Text className={classes.welcomeText}>
              Welcome, {profile?.user?.name || "staff"}
            </Text>
            <Text className={classes.welcomeSubtext}>
              Your compliance dashboard — real-time risk and operational health
            </Text>
          </Box>
          <Box
            className={classes.welcomeClose}
            onClick={() => setShowWelcome(false)}
          >
            <RxCross1 size={16} />
          </Box>
        </Box>
      )}

      <Box className={classes.content}>
        <Tabs value={activeTab} onTabChange={setActiveTab}>
          <Tabs.List className={classes.tabList}>
            <Tabs.Tab className={classes.tab} value="command-center" icon={<IconLayoutDashboard size={16} />}>
              Command Center
            </Tabs.Tab>
            {visibleTabs.includes("operational") && (
              <Tabs.Tab className={classes.tab} value="operational" icon={<IconHeartRateMonitor size={16} />}>
                Operational Health
              </Tabs.Tab>
            )}
            {visibleTabs.includes("business") && (
              <Tabs.Tab className={classes.tab} value="business" icon={<IconChartBar size={16} />}>
                Business Metrics
              </Tabs.Tab>
            )}
          </Tabs.List>

          {/* ─── Command Center (Default) ─── */}
          <Tabs.Panel value="command-center" className={classes.tabPanel}>
            {canSee("dashboardCompliance") && (
              <>
                <ComplianceSnapshot
                  data={dashboardMetrics?.complianceSnapshot}
                  loading={dashboardMetricsLoading}
                  onTileClick={handleTileClick}
                />

                <Box mt="md">
                  <HighRiskProviders
                    data={dashboardMetrics?.highRiskProviders}
                    loading={dashboardMetricsLoading}
                    onProviderClick={handleProviderClick}
                  />
                </Box>

                {/* To-do + Pending Actions live inside the Command Center
                    tab only — they're not relevant to Operational Health or
                    Business Metrics views. */}
                <Grid gutter="xl" mt="md">
                  <Grid.Col xs={12} sm={12} md={8} lg={8}>
                    <ActivityTable />
                  </Grid.Col>
                  <Grid.Col xs={12} sm={12} md={4} lg={4}>
                    <SkypointPendingAction
                      containerClassName={classes.pendingActionContainer}
                    />
                  </Grid.Col>
                </Grid>
              </>
            )}

          </Tabs.Panel>

          {/* ─── Operational Health (Coming soon) ─── */}
          {visibleTabs.includes("operational") && (
            <Tabs.Panel value="operational" className={classes.tabPanel}>
              <ComingSoon
                title="Operational Health"
                description="Workforce, scheduling, and operations metrics across your linked providers — coming soon."
              />
            </Tabs.Panel>
          )}

          {/* ─── Business Metrics (Coming soon) ─── */}
          {visibleTabs.includes("business") && (
            <Tabs.Panel value="business" className={classes.tabPanel}>
              <ComingSoon
                title="Business Metrics"
                description="Prospects, conversion, and contractor-side business analytics — coming soon."
              />
            </Tabs.Panel>
          )}
        </Tabs>
      </Box>

      {selectedDocModal && (
        <SkypointUploadStaffDocDashboard
          selectedFolder={selectedDocModal?.folder}
          userId={selectedDocModal?.userId}
          isPendingAction={true}
          uploadedFiles={selectedDocModal}
        />
      )}
    </Box>
  );
};
